/**
 * Parses the raw markdown output from the parse-flight-schedule Edge Function
 * into structured flight pattern rows for preview.
 */

export interface ScheduleHeader {
  valid_from: string;
  valid_to: string;
  schedule_code: string;
}

export interface FlightPatternRow {
  flight_number: string;
  dep_airport: string;
  dep_time: string;
  arr_airport: string;
  arr_time: string;
  days_bitmask: number;
  days_display: string;
  aircraft_type: string;
  notes: string;
  is_overnight: boolean;
}

export interface ValidatedFlightPatternRow extends FlightPatternRow {
  valid: boolean;
  warnings: string[];
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DAY_MAP: Record<string, number> = {
  sun: 0, sunday: 0,
  mon: 1, monday: 1,
  tue: 2, tuesday: 2,
  wed: 3, wednesday: 3,
  thu: 4, thursday: 4,
  fri: 5, friday: 5,
  sat: 6, saturday: 6,
};

/**
 * Convert a days string like "Mon,Wed,Fri" or "Daily" into a bitmask.
 * bit0=Sun, bit1=Mon, ... bit6=Sat
 */
export function daysToBitmask(daysStr: string): number {
  if (!daysStr || daysStr.trim() === '') return 0;
  const trimmed = daysStr.trim().toLowerCase();
  if (trimmed === 'daily') return 0b1111111; // all 7 days

  let mask = 0;
  const parts = trimmed.split(/[,\s/]+/);
  for (const part of parts) {
    const p = part.trim();
    if (p in DAY_MAP) {
      mask |= (1 << DAY_MAP[p]);
    }
  }
  return mask;
}

/**
 * Convert a bitmask back to human-readable day abbreviations.
 */
export function bitmaskToDays(mask: number): string {
  if (mask === 0b1111111) return 'Daily';
  const days: string[] = [];
  for (let i = 0; i < 7; i++) {
    if (mask & (1 << i)) {
      days.push(DAY_NAMES[i]);
    }
  }
  return days.join(', ');
}

/**
 * Extract the JSON header block from the top of the markdown.
 */
export function extractHeader(markdown: string): ScheduleHeader | null {
  // Look for a JSON object on its own line at the top
  const match = markdown.match(/\{[^}]*"valid_from"[^}]*\}/);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[0]);
    return {
      valid_from: parsed.valid_from || '',
      valid_to: parsed.valid_to || '',
      schedule_code: parsed.schedule_code || '',
    };
  } catch {
    return null;
  }
}

/**
 * Normalize a time string to HH:MM 24hr format.
 */
function normalizeTime(raw: string): string {
  const cleaned = raw.replace(/\s+/g, '').trim();
  // Match HH:MM or HHMM patterns
  const m = cleaned.match(/^(\d{1,2}):?(\d{2})$/);
  if (m) {
    return `${m[1].padStart(2, '0')}:${m[2]}`;
  }
  return cleaned;
}

/**
 * Parse all markdown tables into FlightPatternRow[].
 * Expected columns: | Flight | Dep | From | To | Arr | Days | Notes |
 */
/**
 * Parse HTML <table> rows into FlightPatternRow[].
 */
function parseHtmlTable(html: string): FlightPatternRow[] {
  const rows: FlightPatternRow[] = [];
  const rowMatches = html.matchAll(/<tr>([\s\S]*?)<\/tr>/gi);
  let isHeader = true;

  for (const match of rowMatches) {
    if (isHeader) { isHeader = false; continue; }

    const cells = [...match[1].matchAll(/<td[^>]*>([\s\S]*?)<\/td>/gi)]
      .map(c => c[1].replace(/<[^>]*>/g, '').trim());

    if (cells.length < 5) continue;

    const flight_number = cells[0] || '';
    const dep_time = normalizeTime(cells[1] || '');
    const dep_airport = (cells[2] || '').toUpperCase().substring(0, 3);
    const arr_airport = (cells[3] || '').toUpperCase().substring(0, 3);
    const arr_time = normalizeTime(cells[4] || '');
    const daysStr = cells.length > 5 ? cells[5] : '';
    const notes = cells.length > 6 ? cells[6] : '';

    if (flight_number === '?' || !dep_airport || !arr_airport) continue;

    const days_bitmask = daysToBitmask(daysStr);
    const days_display = bitmaskToDays(days_bitmask);
    const is_overnight = arr_time < dep_time && arr_time !== '' && dep_time !== '';

    rows.push({
      flight_number,
      dep_airport,
      dep_time,
      arr_airport,
      arr_time,
      days_bitmask,
      days_display,
      aircraft_type: '',
      notes,
      is_overnight,
    });
  }

  console.log(`HTML table parser extracted ${rows.length} rows`);
  return rows;
}

/**
 * Parse all markdown tables into FlightPatternRow[].
 * Supports both HTML <table> format (LlamaParse v2) and markdown pipe tables.
 */
/**
 * Inherit flight numbers for EMPTY rows from the row immediately above.
 */
function inheritFlightNumbers(rows: FlightPatternRow[]): FlightPatternRow[] {
  let lastKnownFlightNumber = '';
  return rows.map(row => {
    const fn = row.flight_number.trim().toUpperCase();
    if (fn === 'EMPTY' || fn === '' || fn === '-') {
      return { ...row, flight_number: lastKnownFlightNumber };
    }
    lastKnownFlightNumber = row.flight_number;
    return row;
  });
}

export function parseFlightTables(markdown: string): FlightPatternRow[] {
  let rawRows: FlightPatternRow[];

  // Route to HTML parser if content contains HTML tables
  if (markdown.includes('<table') || markdown.includes('<tr>') || markdown.includes('<tr ')) {
    rawRows = parseHtmlTable(markdown);
  } else {
    rawRows = parseMarkdownPipeTables(markdown);
  }

  // Inherit flight numbers for EMPTY rows before any filtering
  const inherited = inheritFlightNumbers(rawRows);
  console.log(`Flight number inheritance: ${rawRows.length} rows processed`);

  // Filter out rows still without a flight number (top-of-table orphans)
  const filtered = inherited.filter(row =>
    row.flight_number &&
    row.flight_number.trim() !== '' &&
    !['EMPTY', 'empty', '-', 'N/A'].includes(row.flight_number.trim())
  );
  console.log(`Post-inheritance filter: ${inherited.length} → ${filtered.length} rows`);

  return filtered;
}

/**
 * Parse markdown pipe-delimited tables into FlightPatternRow[].
 */
function parseMarkdownPipeTables(markdown: string): FlightPatternRow[] {
  const rows: FlightPatternRow[] = [];
  const lines = markdown.split('\n');

  let currentAircraftType = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Detect section headings for aircraft type
    if (line.startsWith('##')) {
      const headingText = line.replace(/^#+\s*/, '').trim();
      currentAircraftType = headingText;
      continue;
    }

    // Skip non-table lines
    if (!line.startsWith('|')) continue;

    // Skip separator lines
    if (line.match(/^\|[\s\-:|]+\|$/)) continue;

    const cells = line.split('|').map(c => c.trim()).filter(c => c !== '');

    // Skip header rows
    if (cells.length >= 5 && (
      cells[0].toLowerCase() === 'flight' ||
      cells[0].toLowerCase() === 'flt' ||
      cells[0].toLowerCase() === 'flight no'
    )) continue;

    if (cells.length < 5) continue;

    const flight_number = cells[0] || '';
    const dep_time = normalizeTime(cells[1] || '');
    const dep_airport = (cells[2] || '').toUpperCase().substring(0, 3);
    const arr_airport = (cells[3] || '').toUpperCase().substring(0, 3);
    const arr_time = normalizeTime(cells[4] || '');
    const daysStr = cells.length > 5 ? cells[5] : '';
    const notes = cells.length > 6 ? cells[6] : '';

    if (flight_number === '?' || !dep_airport || !arr_airport) continue;

    const days_bitmask = daysToBitmask(daysStr);
    const days_display = bitmaskToDays(days_bitmask);
    const is_overnight = arr_time < dep_time && arr_time !== '' && dep_time !== '';

    let aircraft = currentAircraftType;
    if (notes) {
      const acMatch = notes.match(/\b(F\d+|DH\d+|Dash\s*\d+|B\d+|CRJ\d*|ATR\d*|Q\d+)\b/i);
      if (acMatch) {
        aircraft = acMatch[1];
      }
    }

    rows.push({
      flight_number,
      dep_airport,
      dep_time,
      arr_airport,
      arr_time,
      days_bitmask,
      days_display,
      aircraft_type: aircraft,
      notes,
      is_overnight,
    });
  }

  return rows;
}

/**
 * Merge rows that represent the same flight on different days into a single row
 * with a combined days_bitmask. Used for seasonal multi-column timetables where
 * the AI outputs one row per flight per day.
 *
 * Returns { merged, rawCount, mergedDayValues } for logging.
 */
export function mergeDayRows(rows: FlightPatternRow[]): {
  merged: FlightPatternRow[];
  rawCount: number;
  mergedDayValues: number;
} {
  const rawCount = rows.length;
  const groupMap = new Map<string, FlightPatternRow>();

  for (const row of rows) {
    const key = `${row.flight_number}|${row.dep_airport}|${row.dep_time}`;
    const existing = groupMap.get(key);
    if (existing) {
      existing.days_bitmask |= row.days_bitmask;
      existing.days_display = bitmaskToDays(existing.days_bitmask);
      // Keep first row's other fields (arr_time, aircraft, notes, etc.)
    } else {
      groupMap.set(key, { ...row });
    }
  }

  const merged = Array.from(groupMap.values());
  const mergedDayValues = rawCount - merged.length;

  console.log(`Day merge: ${rawCount} raw rows → ${merged.length} unique patterns (${mergedDayValues} day values merged)`);
  return { merged, rawCount, mergedDayValues };
}

/**
 * Validate parsed rows against existing city codes from Supabase.
 */
export function validateRows(
  rows: FlightPatternRow[],
  validCityCodes: Set<string>
): ValidatedFlightPatternRow[] {
  return rows.map(row => {
    const warnings: string[] = [];

    if (!validCityCodes.has(row.dep_airport)) {
      warnings.push(`dep airport '${row.dep_airport}' not found in cities`);
    }
    if (!validCityCodes.has(row.arr_airport)) {
      warnings.push(`arr airport '${row.arr_airport}' not found in cities`);
    }

    return {
      ...row,
      valid: warnings.length === 0,
      warnings,
    };
  });
}
